import {
  type Entity,
  getSessionId,
  init,
  type LogClient,
  type Metric,
  type MonsterversePerfStats,
  type ProductEvent,
  setSessionId,
} from "@classdojo/log-client";
import pick from "lodash/pick";

import { type AppMetadata, appVersionFromUserAgent, platformFromUserAgent } from "../appBridge/AppMetadata";
import { UserManager } from "../controllers/UserManager";
import { getLauncherStartupOptions } from "../controllers/Utility";
import { USER_FEATURE_SWITCHES } from "../featureSwitches/constants";
import type { IFeatureSwitchManager } from "../featureSwitches/IFeatureSwitchManager";
import { AutoFlushingQueue } from "../utils/AutoFlushingQueue";
import { Env } from "../utils/env";
import { IslandType } from "../utils/IslandType";
import { convertToSeconds } from "../utils/NumberExtension";
import { createWindowTimerFunctions } from "../utils/Timer";

type LogMethod = "info" | "warn" | "error" | "exception";

export class Logger {
  private static logClient: LogClient;
  private static featureSwitchManager: IFeatureSwitchManager;
  static appMetadata: AppMetadata = {};
  private static fleetIQInstanceID?: string;
  private static serverID?: string;
  private static islandType?: string;
  private static countryCode?: string;
  private static playerCoordinates?: { x: number; y: number; z: number };
  private static connectedUsers?: number;
  private static activityId?: string;
  private static webGlVersion?: string;
  private static webGlContextLost?: boolean;
  private static windowVisible?: boolean;
  private static windowVisibleChanged?: number;
  private static ugcReportModalVisible: boolean;
  private static appReadyTime = -1;
  private static lastProductEventSent: ProductEvent | undefined;
  private static lastProductEventTime = -1;
  private static readonly undefinedStr = "--";
  private static _eventsQueue: AutoFlushingQueue<ProductEvent>;
  private static _perfStatsQueue: AutoFlushingQueue<MonsterversePerfStats>;
  private static _metricsQueue: AutoFlushingQueue<Metric>;
  private static _hostAppVersion?: string;
  private static classYear?: string;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  static initialize(
    featureSwitchManager: IFeatureSwitchManager,
    options: {
      env: Env;
      customEndpoint?: string;
      buildNumber?: string;
      appVersion?: string;
      server?: boolean;
      disabledForTest?: boolean;
      sessionId?: string;
      entityId?: string;
    }
  ) {
    const { env, customEndpoint, buildNumber, appVersion, server, disabledForTest, sessionId, entityId } = options;
    this.featureSwitchManager = featureSwitchManager;
    if (!Logger.logClient) {
      Logger.logClient = init({
        initNewInstance: true,
        env: env === Env.Prod ? "prod" : "dev",
        site: "monsterworld",
        customEndpoint,
        buildNumber: buildNumber || window.build,
        appVersion: appVersion || window.build,
        devParseStubbedLogs: true,
        disabledForTest,
        sessionId,
        entityId,
      });
      Logger._eventsQueue = new AutoFlushingQueue<ProductEvent>(60000, 50, (logs) => {
        try {
          Logger.logClient.logMonsterverseProductEvents(logs);
        } catch (e) {
          Logger.logException(new Error("Failed to logMonsterverseEvents"));
        }
      });
      Logger._perfStatsQueue = new AutoFlushingQueue<MonsterversePerfStats>(60000, 50, (stats) =>
        Logger.logClient.logMonsterversePerfStats(stats)
      );
      Logger._metricsQueue = new AutoFlushingQueue<Metric>(60000, 50, (metrics) => Logger.logMetrics(metrics));
      Logger._hostAppVersion = appVersionFromUserAgent();

      if (typeof document !== "undefined" && document?.addEventListener) {
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "hidden") {
            Logger.flushLogs();
          }
        });
      }

      //Disable most console logging when in production (only keeping error for debugging purposes - and its very limited amount of prints)
      if (env === Env.Prod && !server) {
        const ignoreLogs = () => {
          //NOOP
        };
        console.log = ignoreLogs;
        console.info = ignoreLogs;
        console.warn = ignoreLogs;
        console.debug = ignoreLogs;
        window.time = ignoreLogs;
        window.timeEnd = ignoreLogs;
      } else {
        createWindowTimerFunctions();
      }
    }

    Logger.fleetIQInstanceID = undefined;
    Logger.serverID = undefined;
    Logger.islandType = undefined;
    Logger.countryCode = undefined;
    Logger.playerCoordinates = undefined;
    Logger.connectedUsers = undefined;
    Logger.webGlVersion = undefined;
    Logger.webGlContextLost = undefined;
    Logger.windowVisible = undefined;
    Logger.windowVisibleChanged = undefined;
    Logger.ugcReportModalVisible = false;
  }

  static setClassYear(year: string) {
    Logger.classYear = year;
  }
  static getClassYear() {
    return Logger.classYear;
  }
  static setAppReadyTime(time: number) {
    Logger.appReadyTime = time;
  }

  static setFeatureSwitches(featureSwitches: Record<string, string>) {
    Logger.logClient.featureSwitches = featureSwitches;
  }

  static setProductArea(productArea: string) {
    Logger.logClient.productArea = productArea;
  }

  static setServerFleetIQInstanceID(fleetIQInstanceID: string) {
    Logger.fleetIQInstanceID = fleetIQInstanceID;
  }

  static setServerID(serverID: string) {
    Logger.serverID = serverID;
  }

  static setIslandType(islandType: string) {
    Logger.islandType = islandType;
  }

  static setCountryCode(countryCode: string) {
    Logger.countryCode = countryCode;
  }

  static setPlayerCoordinates(coords: { x: number; y: number; z: number }) {
    Logger.playerCoordinates = coords;
  }

  static setConnectedUsers(users: number) {
    Logger.connectedUsers = users;
  }

  static setWebGlVersion(webGlVersion: string) {
    Logger.webGlVersion = webGlVersion;
  }

  static setWebGlContextLost(webGlContextLost: boolean) {
    Logger.webGlContextLost = webGlContextLost;
  }

  static setWindowVisibility(visible: boolean) {
    Logger.windowVisible = visible;
    Logger.windowVisibleChanged = Date.now();
  }

  static setUgcReportModalVisible(visible: boolean) {
    Logger.ugcReportModalVisible = visible;
  }

  static setActivityId(activityId: string) {
    Logger.activityId = activityId;
  }

  static getClassID() {
    if (Logger.islandType === IslandType[IslandType.Class]) {
      return Logger.serverID?.replace(/^class-/, "");
    }
    return undefined;
  }

  static logEvent(rawData: string | ProductEvent, { appendEntityType = true } = {}) {
    if (typeof rawData === "string") {
      rawData = { eventName: rawData };
    }

    Logger.lastProductEventSent = rawData;
    Logger.lastProductEventTime = Date.now();

    if (Logger.fleetIQInstanceID) {
      rawData.metadata = { ...rawData.metadata, fleetIQInstanceID: Logger.fleetIQInstanceID };
    }
    if (Logger.serverID) {
      rawData.metadata = { ...rawData.metadata, serverID: Logger.serverID };
    }
    if (Logger.appMetadata.hostId) {
      rawData.metadata = { ...rawData.metadata, hostId: Logger.appMetadata.hostId };
    }
    if (Logger.islandType) {
      rawData.metadata = { ...rawData.metadata, islandType: Logger.islandType };

      // For Class islands, also log the classID and island state
      if (Logger.islandType === IslandType[IslandType.Class]) {
        rawData.metadata = { ...rawData.metadata, classIslandState: { activityId: Logger.activityId } };

        if (Logger.serverID) {
          const classId = Logger.getClassID();
          rawData.metadata = { ...rawData.metadata, classId };
        }
      }
    }

    if (Logger.countryCode) {
      rawData.metadata = { ...rawData.metadata, countryCode: Logger.countryCode };
    }
    if (Logger.playerCoordinates) {
      rawData.metadata = {
        ...rawData.metadata,
        coordinates: { ...Logger.playerCoordinates },
      };
    }
    if (Logger.connectedUsers != undefined) {
      rawData.metadata = { ...rawData.metadata, connectedUsers: Logger.connectedUsers };
    }
    if (Logger.webGlVersion) {
      rawData.metadata = { ...rawData.metadata, webGlVersion: Logger.webGlVersion };
    }
    if (Logger.webGlContextLost !== undefined) {
      rawData.metadata = { ...rawData.metadata, webGlContextLost: Logger.webGlContextLost };
    }
    if (Logger.windowVisible !== undefined) {
      rawData.metadata = { ...rawData.metadata, windowVisible: Logger.windowVisible };
    }
    if (Logger.windowVisibleChanged !== undefined) {
      rawData.metadata = {
        ...rawData.metadata,
        windowVisibleChanged: convertToSeconds(Date.now() - Logger.windowVisibleChanged),
      };
    }

    const entityTypePrefix = Logger.getEntityTypePrefix(rawData.eventName, appendEntityType);

    const platform = this.getPlatform();
    const eventName = `${platform}.${entityTypePrefix}${rawData.eventName}`;
    rawData.eventName = eventName;

    this._eventsQueue.push({
      clientCreatedAt: new Date(),
      ...pick(this.appMetadata, "osVersion", "device", "deviceID", "deviceModel"),
      ...rawData,
    });
  }

  public static getPlatform(): string {
    // sets the platform to a sensible default based on the user agent until metadata is synced
    const defaultPlatform = platformFromUserAgent();
    const platform = this.appMetadata?.platform || defaultPlatform;
    return platform.toLocaleLowerCase();
  }

  private static getEntityTypePrefix(eventName: string, appendEntityType: boolean) {
    let entityTypePrefix = "";
    if (appendEntityType) {
      if (eventName.startsWith("teacher.") || eventName.startsWith("student.")) {
        throw new Error(
          `Don't use 'student.' or 'teacher.' prefixes for ${eventName}. They will be added based on the entityType`
        );
      }

      if (!Logger.logClient.entityType) {
        // try to get entity type from startup query string params
        const { entityType } = getLauncherStartupOptions();
        entityTypePrefix = `${entityType}.`;
      } else {
        entityTypePrefix =
          Logger.logClient.entityType === "studentUser" ? "student." : `${Logger.logClient.entityType}.`;
      }
    }

    return entityTypePrefix;
  }

  static shouldLogExperiment(featureSwitch: string) {
    // ignore experiments for other platforms
    if (!this.featureSwitchManager.isValidFeatureSwitchForCurrentPlatform(featureSwitch)) {
      return false;
    }

    // if we fetched the feature switch, we should def log it
    if (this.featureSwitchManager.featureSwitches && this.featureSwitchManager.featureSwitches[featureSwitch]) {
      return true;
    }

    // Otherwise, we didn't fetch the FS, so don't log any experiment conversions.
    // If it's a user switch, that should never happen, so log a warning.
    if (Object.values(USER_FEATURE_SWITCHES).includes(featureSwitch)) {
      Logger.logWarn(`User Feature Switch ${featureSwitch} was not fetched`);
    }

    return false;
  }

  static logStartExperiment(experiment: string) {
    // ignore experiments for other platforms
    if (Logger.shouldLogExperiment(experiment)) {
      console.log("%c logStartExperiment", "color: blue;", experiment);
      Logger.logClient.logStartExperiment(experiment);
    }
  }

  static logStartExperimentWithOverrides(experiment: string, variant: string, entityId: string, entityType: Entity) {
    if (this.featureSwitchManager.featureSwitches && this.featureSwitchManager.featureSwitches[experiment]) {
      console.log("%c logStartExperimentWithOverrides", "color: blue;", experiment, variant, entityId, entityType);
      Logger.logClient.logStartExperimentWithOverrides(experiment, variant, entityId, entityType);
    }
  }

  static logConvertExperiment(experiment: string) {
    if (Logger.shouldLogExperiment(experiment)) {
      console.log("%c logConvertExperiment", "color: blue;", experiment);
      Logger.logClient.logConvertExperiment(experiment);
    }
  }

  static logConvertExperimentWithOverrides(experiment: string, variant: string, entityId: string, entityType: Entity) {
    if (this.featureSwitchManager.featureSwitches && this.featureSwitchManager.featureSwitches[experiment]) {
      console.log("%c logConvertExperimentWithOverrides", "color: blue;", experiment, variant, entityId, entityType);
      Logger.logClient.logConvertExperimentWithOverrides(experiment, variant, entityId, entityType);
    }
  }

  static logException(error: Error, additionalContext: Record<string, unknown> = {}) {
    if (window.iosCancelButtonPressed) {
      return;
    }

    Logger.logClient.logException(error, this.getExtendedLogContext(additionalContext));
  }

  static logInfo(message: string, context: Record<string, unknown> = {}) {
    Logger.logMessage("info", message, context);
    console.log(message, context);
  }

  static logWarn(message: string, context: Record<string, unknown> = {}) {
    Logger.logMessage("warn", message, context);
    console.warn(message, context);
  }

  static logError(message: string, context: Record<string, unknown> = {}) {
    Logger.logMessage("error", message, context);
    console.error(message, context);
  }

  private static logMessage(
    verbosity: "error" | "warn" | "info",
    message: string,
    context: Record<string, unknown> = {}
  ) {
    Logger.logClient.logMonsterverseMessage({
      message,
      verbosity,
      context: this.getExtendedLogContext(context),
    });
  }

  private static getExtendedLogContext(context: Record<string, unknown> | undefined = undefined) {
    return {
      userAgent: window.navigator.userAgent,
      deviceModel: this.appMetadata.deviceModel,
      hostAppVersion: this._hostAppVersion,
      platform: Logger.getPlatform(),
      sessionId: getSessionId(),
      userType: UserManager.instance.user?.type,
      serverID: this.serverID,
      hostId: Logger.appMetadata.hostId,
      islandType: this.islandType,
      entityId: Logger.logClient.entityId,
      schoolID: UserManager.instance.getSchoolId(),
      countryCode: this.countryCode,
      connectedUsers: this.connectedUsers,
      webGlVersion: this.webGlVersion,
      webGlContextLost: this.webGlContextLost,
      windowVisible: this.windowVisible,
      windowVisibleChanged: this.windowVisibleChanged
        ? convertToSeconds(Date.now() - this.windowVisibleChanged)
        : undefined,
      ugcReportModalVisible: this.ugcReportModalVisible,
      appState: {
        activityId: Logger.activityId,
        secondsSinceAppReady:
          this.appReadyTime > 0 ? convertToSeconds(Date.now() - this.appReadyTime) : this.undefinedStr,
        lastProductEvent: {
          event: this.lastProductEventSent,
          secondsSinceEvent: this.lastProductEventSent
            ? convertToSeconds(Date.now() - this.lastProductEventTime)
            : this.undefinedStr,
        },
        featureSwitches: this.featureSwitchManager.featureSwitches,
      },
      // NOTE: This is adding a lot of extra info to all our logs.
      // We decided to remove it for now. More context: https://classdojo.slack.com/archives/C05EPH2M0F5/p1714590671690819
      //...debugInfoSystem.getDebugInfo(),
      ...context,
    };

    // Some other values we may want to keep track of:

    // - total # of players
    // - local player coords
    // - memory usage (if available)
  }

  static printAppState() {
    console.log(this.getExtendedLogContext());
  }

  static logPerfStats(stats: MonsterversePerfStats) {
    stats.deviceModel = stats.deviceModel ?? this.appMetadata.deviceModel;
    if (Logger.appMetadata.hostId) {
      stats.hostId = Logger.appMetadata.hostId;
    }
    if (Logger.islandType === IslandType[IslandType.Home]) {
      stats.site = IslandType[IslandType.Home];
    }
    Logger._perfStatsQueue.push(stats);
  }

  /**
   * @summary Only throws an exception when working in developer mode, but still logs the exception to our logging services.
   * @remarks Optionally, use the logMethod parameter to change the verbosity of the reporting to the backend.
   * @param error Error object or string to be thrown.
   * @param logMethodOnProd Logging method to backend used to control what verbosity we wish to report.
   */
  static throwIfTesting(error: Error, logMethodOnProd?: LogMethod): void;
  static throwIfTesting(error: string, logMethodOnProd?: LogMethod): void;
  static throwIfTesting(error: Error | string, logMethodOnProd: LogMethod = "exception"): void {
    let throwable: Error | undefined;
    if (error instanceof Error) {
      throwable = error;
    } else if (typeof error === "string" || (error as unknown) instanceof String) {
      throwable = new Error(error);
    }

    if (throwable === undefined) {
      return;
    }

    if (Logger.logClient.env !== "prod") {
      throw throwable;
    }

    switch (logMethodOnProd) {
      default:
      case "info":
        Logger.logInfo(throwable.toString(), this.getExtendedLogContext({ ...throwable }));
        break;
      case "warn":
        Logger.logWarn(throwable.toString(), this.getExtendedLogContext({ ...throwable }));
        break;
      case "error":
        Logger.logError(throwable.toString(), this.getExtendedLogContext({ ...throwable }));
        break;
      case "exception":
        Logger.logException(
          throwable,
          this.getExtendedLogContext({
            ...throwable,
          })
        );
        break;
    }
  }

  static logMetrics(metrics: Metric[]) {
    if (metrics.length) {
      Logger.logClient?.logMetrics(metrics);
    }
  }

  static incrementMetric(metricName: string, value = 1, tags?: Record<string, string>) {
    this._metricsQueue.push({
      type: "increment",
      metricName,
      value,
      tags: { platform: Logger.getPlatform(), islandType: Logger.islandType ?? "unknown", ...tags },
    });
  }

  static flushLogs() {
    Logger._eventsQueue.flush();
    Logger._perfStatsQueue.flush();
    Logger._metricsQueue.flush();
    Logger.logClient.flush();
  }

  static setEntityId(id?: string) {
    if (Logger.logClient) Logger.logClient.entityId = id;
  }

  static setEntityType(entityType?: Entity) {
    if (Logger.logClient) Logger.logClient.entityType = entityType;
  }

  static getSessionId() {
    return getSessionId();
  }

  static setSessionId(sessionId: string) {
    setSessionId(sessionId);
  }
}
